import { AnimatePresence, motion } from 'motion/react'
import type { ActivityEntry, Discipline } from '@/hooks/useDashboardChannel'
import { timeAgo } from '@/lib/format'
import { workoutNouns } from '@/lib/discipline'
import { EASE } from '@/components/dashboard/fx/motion'

interface LiveFeedProps {
  entries: ActivityEntry[]
  discipline: Discipline
}

const VISIBLE = 7

// New entries slide in at the top and push the rest down (layout
// animation); the oldest fade out the bottom.
export function LiveFeed({ entries, discipline }: LiveFeedProps) {
  if (entries.length === 0) {
    const { singular } = workoutNouns(discipline)
    return <p className="py-6 text-muted-foreground">Waiting for the first {singular} to sync...</p>
  }

  return (
    <ul className="flex flex-col">
      <AnimatePresence initial={false} mode="popLayout">
        {entries.slice(0, VISIBLE).map((entry, index) => (
          <motion.li
            key={entry.id}
            layout
            initial={{ opacity: 0, y: -28, filter: 'blur(6px)' }}
            animate={{ opacity: 1 - index * 0.09, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="flex items-center gap-4 border-b border-border/60 py-4 first:pt-0 last:border-0"
          >
            <span className="relative flex size-2.5 shrink-0">
              {index === 0 && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />}
              <span className={`relative inline-flex size-2.5 rounded-full ${index === 0 ? 'bg-primary' : 'bg-muted-foreground/40'}`} />
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-display text-lg font-medium tracking-tight sm:text-xl">{entry.title}</span>
              <span className="truncate text-sm text-muted-foreground">
                {entry.instructor_name ?? 'Unknown instructor'} · {entry.song_count} songs
              </span>
            </div>
            <span className="shrink-0 font-mono text-xs uppercase tracking-wider text-muted-foreground">
              {index === 0 ? 'just now' : timeAgo(entry.synced_at)}
            </span>
          </motion.li>
        ))}
      </AnimatePresence>
    </ul>
  )
}
